import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  fetchStreakAuditReport,
  getLastSearchedUserId,
  getStreakAuditPayload,
  isStreakAuditApiSuccess,
  setLastSearchedUserId,
} from '../../services/streakAuditApi';
import { DAYS_PRESETS } from './streakAuditUtils';
import StreakDiagnosisBanner from './StreakDiagnosisBanner';
import StreakSummaryCards from './StreakSummaryCards';
import StreakLastBreakPanel from './StreakLastBreakPanel';
import StreakTimelineTable from './StreakTimelineTable';
import StreakAuditLogTable from './StreakAuditLogTable';
import StreakDebugLogTable from './StreakDebugLogTable';
import StreakIssuesList from './StreakIssuesList';

function StreakAuditDashboard() {
  const [userIdInput, setUserIdInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showInactiveDays, setShowInactiveDays] = useState(false);

  useEffect(() => {
    const lastUserId = getLastSearchedUserId();
    if (lastUserId) {
      setUserIdInput(String(lastUserId));
    }
  }, []);

  const loadReport = useCallback(async () => {
    const userId = Number(userIdInput);
    if (!userIdInput || !Number.isInteger(userId) || userId <= 0) {
      setError('Enter a valid numeric user ID.');
      return;
    }

    if (fromDate && toDate && fromDate > toDate) {
      setError('From date must be on or before to date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const filters = { userId, days };
      if (fromDate) filters.fromDate = fromDate;
      if (toDate) filters.toDate = toDate;

      const result = await fetchStreakAuditReport(filters);
      if (!isStreakAuditApiSuccess(result)) {
        setError(result?.message || 'Failed to load streak audit report.');
        setReport(null);
        return;
      }

      const data = getStreakAuditPayload(result);
      setReport(data);
      setLastSearchedUserId(userId);
      setActiveTab(0);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load streak audit report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [userIdInput, fromDate, toDate, days]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      loadReport();
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center">
                  <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>Streak Audit</h3>
                </div>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 7, mt: 0.5 }}>
                  Debug app streak breaks for a specific user
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="User ID"
                        value={userIdInput}
                        onChange={(e) => setUserIdInput(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. 13143"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="From date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="To date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Days preset"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        disabled={Boolean(fromDate)}
                        helperText={fromDate ? 'Using from date' : 'Used when no from date'}
                      >
                        {DAYS_PRESETS.map((preset) => (
                          <MenuItem key={preset.value} value={preset.value}>
                            {preset.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                        onClick={loadReport}
                        disabled={loading}
                      >
                        Load report
                      </Button>
                    </Grid>
                  </Grid>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  {loading && !report && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {!loading && !report && !error && (
                    <Alert severity="info">
                      Enter a user ID to investigate streak activity.
                    </Alert>
                  )}

                  {report && (
                    <>
                      {report.dateRange && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Report range: {report.dateRange.from} to {report.dateRange.to}
                          {report.generatedAt && ` · Generated ${new Date(report.generatedAt).toLocaleString()}`}
                        </Typography>
                      )}

                      <StreakDiagnosisBanner report={report} />
                      <StreakLastBreakPanel lastStreakBreak={report.summary?.lastStreakBreak} />
                      <StreakSummaryCards report={report} />

                      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                          <Tab label="Timeline" />
                          <Tab label={`Audit log (${report.recentAuditEntries?.length || 0})`} />
                          <Tab label={`Debug logs (${report.recentDebugLogs?.length || 0})`} />
                          <Tab label={`Issues (${report.summary?.suspectedIssues?.length || 0})`} />
                        </Tabs>
                      </Box>

                      {activeTab === 0 && (
                        <StreakTimelineTable
                          timeline={report.timeline || []}
                          showInactiveDays={showInactiveDays}
                          onToggleInactive={setShowInactiveDays}
                        />
                      )}
                      {activeTab === 1 && (
                        <StreakAuditLogTable entries={report.recentAuditEntries || []} />
                      )}
                      {activeTab === 2 && (
                        <StreakDebugLogTable logs={report.recentDebugLogs || []} />
                      )}
                      {activeTab === 3 && (
                        <StreakIssuesList issues={report.summary?.suspectedIssues || []} />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default StreakAuditDashboard;
